'use client';

/* eslint-disable @next/next/no-img-element */
import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Lightbox, { type RenderSlideProps } from 'yet-another-react-lightbox';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import '@eliadmin/core/src/components/lightbox/lightbox-styles-override.css';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import type { InstagramMedia } from '@eliadmin/core/src/api/instagram/api';
import { fetchInstagramMedia } from '@eliadmin/core/src/api/instagram/api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { IoClose } from 'react-icons/io5';
import { FaHeart, FaInstagram } from 'react-icons/fa6';
import { HiDotsVertical } from 'react-icons/hi';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

const INSTAGRAM_URL = 'https://www.instagram.com/ubytovaniestrba/';

export function Socials() {
  const { ref, hasIntersected } = useHasIntersected<HTMLElement>();
  const { data, isError, isFetched, isFetching } = useQuery({
    queryKey: ['INSTAGRAM_MEDIA', 'ubytovaniestrba'],
    queryFn: () => fetchInstagramMedia({ results: 9 }),
    enabled: hasIntersected,
    staleTime: Infinity,
  });

  const posts = data?.data ?? [];
  const isLoading = !posts.length && (!isFetched || isFetching);
  const cards = posts.length ? posts : getLoadingPosts();
  const imagePosts = posts.filter(item => item.media_type !== 'VIDEO');
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [videoPost, setVideoPost] = useState<InstagramMedia | null>(null);

  if ((isFetched || isError) && !posts.length) {
    return null;
  }

  return (
    <section
      ref={ref}
      className={cn('custom-section', 'px-0', 'section-padding-lg-center', 'generic-gap')}
    >
      <InstagramImageLightbox
        posts={imagePosts}
        selectedIndex={selectedImageIndex}
        setSelectedIndex={setSelectedImageIndex}
      />
      <VideoLightbox post={videoPost} onClose={() => setVideoPost(null)} />
      <div className="flex flex-col items-center justify-center gap-6 px-6 md:px-0">
        <Heading title="Sledujte nás<br/>na sociálnych sieťach" center />
        <Paragraph className="w-full max-w-[588px] text-center" size="lg">
          Atmosféra, ročné obdobia, hostia aj zákulisie - denne zdieľame momenty z Chata Horec a
          bytu RYSY.
        </Paragraph>
      </div>
      <div className="socials-marquee-track relative w-full overflow-hidden">
        <div
          className="socials-marquee-inner flex gap-5"
          style={{
            animation: 'socials-marquee 32s linear infinite',
            willChange: 'transform',
          }}
        >
          {[...cards, ...cards].map((card, index) => (
            <SocialCard card={card} isLoading={isLoading} key={`${card.id}-${index}`} />
          ))}
        </div>
        <style>{`
          @keyframes socials-marquee {
            from { transform: translateX(0); }
            to   { transform: translateX(-50%); }
          }
          .socials-marquee-track:hover .socials-marquee-inner {
            animation-play-state: paused;
          }
        `}</style>
      </div>
      <div className="flex items-center justify-center px-6 md:px-0">
        <Button asChild className="w-full sm:w-fit">
          <Link prefetch={false} href={INSTAGRAM_URL} target="_blank">
            <FaInstagram size={20} className="text-secondary" />
            Sledovať na Instagrame
          </Link>
        </Button>
      </div>
    </section>
  );
}

function SocialCard({ card, isLoading }: { card: InstagramMedia; isLoading: boolean }) {
  const isDesktop = useBreakpoint('sm');
  const caption = card.caption?.trim();

  return (
    <Link
      href={card?.permalink ?? '#'}
      target="_blank"
      className="flex min-w-[260px] max-w-[260px] flex-col items-center justify-center rounded-[16px] border text-left transition-all hover:shadow-md disabled:cursor-default disabled:hover:shadow-none sm:min-w-[300px] sm:max-w-[300px] lg:min-w-[360px] lg:max-w-[360px]"
      aria-label={caption || 'Otvoriť Instagram príspevok'}
    >
      <div className="flex w-full items-center justify-between p-3 sm:p-4">
        <div className="flex items-center justify-start gap-[10px]">
          <Image
            src={'/images/icons/strba.svg'}
            alt="Štrba"
            width={isDesktop ? 40 : 32}
            height={isDesktop ? 40 : 32}
            className="shrink-0 object-contain"
          />
          <div className="flex flex-col items-start justify-start gap-[2px]">
            <p className="text-sm font-bold leading-[130%] tracking-tight text-secondary sm:text-base sm:leading-[130%]">
              Ubytovanie Štrba
            </p>
            <p className="text-[10px] font-normal leading-[130%] text-secondary sm:text-xs sm:leading-[130%]">
              @ubytovaniestrba
            </p>
          </div>
        </div>
        <HiDotsVertical size={16} className="text-secondary" />
      </div>
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted">
        {card.media_type === 'VIDEO' ? (
          <video
            muted
            loop
            autoPlay
            playsInline
            preload="metadata"
            src={card.media_url}
            className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          />
        ) : (
          <img
            src={card.media_url}
            alt={caption || `Instagram post ${card.id}`}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            className={cn(
              'absolute inset-0 z-0 h-full w-full object-cover object-center',
              isLoading && 'animate-pulse opacity-40',
            )}
          />
        )}
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-1 p-3 sm:gap-2 sm:p-4">
        <div className="flex items-center justify-start gap-2">
          <FaHeart size={18} className="text-[#FA428F]" />
          <p className="sm:text-balances text-sm font-bold leading-[100%] text-secondary sm:leading-[100%]">
            {card.like_count} páči sa
          </p>
        </div>
        <p className="text-xs font-medium text-secondary sm:text-sm">
          {isLoading ? 'Načítavame príspevok...' : 'Pozrieť všetky komentáre'}
        </p>
      </div>
    </Link>
  );
}

function InstagramImageLightbox({
  posts,
  selectedIndex,
  setSelectedIndex,
}: {
  posts: InstagramMedia[];
  selectedIndex: number | null;
  setSelectedIndex: (index: number | null) => void;
}) {
  return (
    <Lightbox
      open={selectedIndex !== null}
      close={() => setSelectedIndex(null)}
      index={selectedIndex ?? 0}
      slides={posts.map(post => ({
        src: post.media_url,
        alt: post.caption || `Instagram post ${post.id}`,
      }))}
      controller={{ closeOnBackdropClick: true }}
      on={{
        view: ({ index }) => setSelectedIndex(index),
      }}
      render={{
        slide: ({ slide }: RenderSlideProps) => (
          <img
            src={slide.src}
            alt={slide.alt || ''}
            decoding="async"
            fetchPriority="high"
            referrerPolicy="no-referrer"
            className="max-h-full max-w-full object-contain"
          />
        ),
      }}
      plugins={[Thumbnails, Zoom]}
      zoom={{ maxZoomPixelRatio: 2 }}
      styles={{
        container: { zIndex: 999999, pointerEvents: 'auto' },
        root: { zIndex: 999999 },
        button: { zIndex: 1000000 },
      }}
    />
  );
}

function VideoLightbox({ post, onClose }: { post: InstagramMedia | null; onClose: () => void }) {
  const { t } = useTranslations();
  if (!post) return null;

  return (
    <div
      className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/90 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Instagram video"
      onClick={onClose}
    >
      <button
        type="button"
        className="absolute right-4 top-4 z-[1000000] flex h-10 w-10 items-center justify-center rounded-full bg-white text-secondary transition-all hover:bg-white/90"
        onClick={onClose}
        aria-label={t('text1855')}
      >
        <IoClose size={24} />
      </button>
      <video
        controls
        autoPlay
        playsInline
        src={post.media_url}
        className="max-h-[90vh] max-w-[92vw] object-contain"
        onClick={event => event.stopPropagation()}
      />
    </div>
  );
}

function useHasIntersected<TElement extends Element>() {
  const ref = useRef<TElement | null>(null);
  const [hasIntersected, setHasIntersected] = useState(false);

  useEffect(() => {
    const element = ref.current;

    if (!element || hasIntersected) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setHasIntersected(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [hasIntersected]);

  return { ref, hasIntersected };
}

function getLoadingPosts(): InstagramMedia[] {
  return Array.from({ length: 9 }, (_, index) => ({
    id: `loading-${index}`,
    media_type: 'IMAGE',
    media_url: '/images/placeholder.png',
    caption: '',
  }));
}
